// ShareJet bookings list: counters, search / dates / dropdown filters, Upcoming-Past and the table.
import { useLocation, useNavigate } from 'react-router-dom';
import { useUrlParams } from '../../hooks/useUrlParams';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  DatePicker,
  Input,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';

import { getBookingCounts, getBookingFilterOptions, listBookings, updateBooking } from '../../api/endpoints';
import type {
  AdminBookingRow,
  BookingCounts,
  BookingStatus,
  BookingWhen,
} from '../../api/types';
import { COUNTERS, type CounterDef } from './bookingCounters';
import { bookingColumns } from './bookingColumns';

const { Title, Text } = Typography;

/** Admin regression 6 Oct — the widest a filter dropdown's list may grow. */
const FILTER_POPUP_MAX_WIDTH = 360;

export function BookingsListPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const queryClient = useQueryClient();
  // SHAR-848 put the tab and the page in the URL. SHAR-1084 - so is everything
  // else the admin narrows the list with: the search, the dates, the four
  // dropdowns, the status chip and the label cards. "Back" from a booking (the
  // detail page returns to the exact list URL), a reload or a pasted link
  // restores all of it.
  const [sp, setParams] = useUrlParams();
  const q = sp.get('q') ?? '';
  const status = (sp.get('status') || undefined) as BookingStatus | undefined;
  const mgavOverdue = sp.get('mgav') === '1';
  const adhocNewSeats = sp.get('newSeats') === '1';
  const notPaid = sp.get('notPaid') === '1'; // SHAR-163
  const passportNeeded = sp.get('passport') === '1'; // SHAR-451
  // SHAR-1038 — departure date range, as on Charters and Charter bookings.
  const range = { from: sp.get('from') || undefined, to: sp.get('to') || undefined };
  // SHAR-61 — the dropdown filters. SHAR-1072: like the search box and the
  // dates they narrow the counters as well as the rows.
  const fOperator = sp.get('operator') || undefined;
  const fModel = sp.get('model') || undefined;
  const fTail = sp.get('tail') || undefined;
  const fAirport = sp.get('airport') || undefined;
  const flag = (b: boolean) => (b ? '1' : null);
  const setQ = (v: string) => setParams({ q: v || null });
  const setStatus = (v: BookingStatus | undefined) => setParams({ status: v ?? null });
  const setMgavOverdue = (b: boolean) => setParams({ mgav: flag(b) });
  const setAdhocNewSeats = (b: boolean) => setParams({ newSeats: flag(b) });
  const setNotPaid = (b: boolean) => setParams({ notPaid: flag(b) });
  const setPassportNeeded = (b: boolean) => setParams({ passport: flag(b) });
  const setRange = (r: { from?: string; to?: string }) => setParams({ from: r.from ?? null, to: r.to ?? null });
  const setFOperator = (v: string | undefined) => setParams({ operator: v ?? null });
  const setFModel = (v: string | undefined) => setParams({ model: v ?? null });
  const setFTail = (v: string | undefined) => setParams({ tail: v ?? null });
  const setFAirport = (v: string | undefined) => setParams({ airport: v ?? null });
  const when = (sp.get('tab') === 'past' ? 'past' : 'upcoming') as BookingWhen;
  const page = Math.max(1, Number(sp.get('page') ?? 1) || 1);
  const setWhen = (v: BookingWhen) => setParams({ tab: v, page: '1' });
  const setPage = (v: number) => setParams({ page: String(v) });
  // SHAR-1079..1082 — the dropdowns depend on the tab, the dates, the search and
  // each other, so their options are fetched with the same filters.
  const filterOptions = useQuery({
    queryKey: ['bookingFilterOptions', when, range.from, range.to, q, fOperator, fModel, fTail, fAirport],
    queryFn: () =>
      getBookingFilterOptions({
        when,
        from: range.from,
        to: range.to,
        q: q || undefined,
        operatorId: fOperator,
        model: fModel,
        tail: fTail,
        airport: fAirport,
      }),
    placeholderData: (prev) => prev,
  });
  const pageSize = 20;

  // SHAR-1038 — the counters follow the date range; SHAR-1072 — and the search
  // and the dropdowns: every filter of the table except the status chip and the
  // label cards, which pick a counter rather than narrow them. The filters are
  // part of the key, so the counters are fetched again with the list.
  const counts = useQuery({
    queryKey: ['bookingCounts', when, range.from, range.to, q, fOperator, fModel, fTail, fAirport],
    queryFn: () =>
      getBookingCounts({
        when,
        from: range.from,
        to: range.to,
        q: q || undefined,
        operatorId: fOperator,
        model: fModel,
        tail: fTail,
        airport: fAirport,
      }),
  });

  const list = useQuery({
    queryKey: ['bookings', q, status, mgavOverdue, adhocNewSeats, notPaid, passportNeeded, when, page, range.from, range.to, fOperator, fModel, fTail, fAirport],
    queryFn: () =>
      listBookings({
        q: q || undefined,
        status,
        mgavOverdue: mgavOverdue || undefined,
        adhocNewSeats: adhocNewSeats || undefined,
        notPaid: notPaid || undefined,
        passportNeeded: passportNeeded || undefined, // SHAR-451
        when,
        page,
        pageSize,
        from: range.from,
        to: range.to,
        operatorId: fOperator,
        model: fModel,
        tail: fTail,
        airport: fAirport,
      }),
  });

  // Remove an ad-hoc label (MGAV overdue / New seats booked) from a booking,
  // then refresh the affected list + counters.
  const removeAdhoc = useMutation({
    mutationFn: (v: {
      id: string;
      body: {
        dismissMgavOverdue?: boolean;
        clearNewSeats?: boolean;
        dismissPassportNeeded?: boolean;
        dismissPaymentRequired?: boolean; // SHAR-1088
      };
    }) => updateBooking(v.id, v.body),
    onSuccess: (_data, v) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookingCounts'] });
      // SHAR-1105 — and the booking's own page, so the label is gone there too
      // (the admin caches every query for 30 s).
      queryClient.invalidateQueries({ queryKey: ['booking', v.id] });
    },
  });

  // Which counter is currently active, for the highlight.
  const activeKey: keyof BookingCounts = passportNeeded
    ? 'passportNeeded'
    : notPaid
    ? 'notPaid'
    : adhocNewSeats
    ? 'newSeats'
    : mgavOverdue
    ? 'mgavOverdue'
    : // status is only ever a counter status (never the computed 'complete').
      ((status ?? 'total') as keyof BookingCounts);

  function selectCounter(def: CounterDef) {
    // Reset every mutually-exclusive filter, then set the one that was clicked.
    setMgavOverdue(false);
    setAdhocNewSeats(false);
    setNotPaid(false);
    setPassportNeeded(false);
    setStatus(undefined);
    if (def.mgav) {
      setMgavOverdue(true);
    } else if (def.newSeats) {
      setAdhocNewSeats(true);
    } else if (def.notPaid) {
      setNotPaid(true);
    } else if (def.passportNeeded) {
      setPassportNeeded(true); // SHAR-451
    } else {
      setStatus(def.status);
    }
    setPage(1);
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Title level={3} style={{ margin: 0 }}>Bookings</Title>
        <Text type="secondary">Seat bookings across all flights brokered by ShareJet.</Text>
      </div>

      {/* Status counters — stretched to fill the full row width. */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          width: '100%',
        }}
      >
        {COUNTERS.map((c) => {
          const active = activeKey === c.key;
          return (
            <Card
              key={c.key}
              size="small"
              hoverable
              loading={counts.isLoading}
              onClick={() => selectCounter(c)}
              style={{
                // Regression sweep, 31 Aug: the status pill inside the card is
                // one line and does not shrink, so a long label ("Gathering
                // passengers") ran past the card's right edge at 130. The basis
                // fits the longest label the statuses can produce, and the pill
                // is allowed to wrap if a longer one is ever added.
                flex: '1 1 168px',
                minWidth: 168,
                cursor: 'pointer',
                borderColor: active ? '#1677ff' : undefined,
                boxShadow: active ? '0 0 0 1px #1677ff' : undefined,
              }}
              styles={{ body: { padding: '12px 16px' } }}
            >
              <Statistic
                title={
                  c.color ? (
                    <Tag color={c.color} style={{ marginInlineEnd: 0, whiteSpace: 'normal' }}>
                      {c.label}
                    </Tag>
                  ) : (
                    c.label
                  )
                }
                value={counts.data?.[c.key] ?? 0}
              />
            </Card>
          );
        })}
      </div>

      <Card>
        {/* Search (left) + Upcoming/Past Segmented (far right) on one row. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            // SHAR-1039 — search and date range sit together on the left; only
            // the Upcoming/Past switch is pushed to the right (marginLeft auto).
            justifyContent: 'flex-start',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search by user, route, flight no."
            defaultValue={q}
            onPressEnter={(e) => {
              setQ((e.target as HTMLInputElement).value);
              setPage(1);
            }}
            onChange={(e) => {
              if (!e.target.value) {
                setQ('');
                setPage(1);
              }
            }}
            style={{ flex: '2 1 220px', minWidth: 200, maxWidth: 360 }}
            allowClear
          />
          {/* SHAR-1038 — the departure date range, beside the search, the same
              control the Charters and Charter bookings pages have. Both ends are
              inclusive and either may be left empty. */}
          <DatePicker.RangePicker
            allowEmpty={[true, true]}
            value={[range.from ? dayjs(range.from) : null, range.to ? dayjs(range.to) : null]}
            format="DD/MM/YYYY"
            placeholder={['Departure from', 'Departure to']}
            onChange={(vals) => {
              const iso = (d: Dayjs | null | undefined) => (d ? d.format('YYYY-MM-DD') : undefined);
              setRange({ from: iso(vals?.[0]), to: iso(vals?.[1]) });
              setPage(1);
            }}
            // Admin regression 6 Oct — 230-250px cut the placeholder to
            // "Departure f…"; 280 fits "Departure from" / "Departure to".
            style={{ flex: '0 1 300px', minWidth: 280 }}
          />
          {/* SHAR-61 — autocomplete dropdowns: operator, model, tail number,
              airport (either end of the route); each clears with its own "x".
              SHAR-1078 — in the same row as the search and the dates; on a
              screen too narrow for all of them the row wraps instead of
              running off the edge. SHAR-1079..1082 — each offers only the values
              the other filters and the tab leave. */}
          {(
            [
              ['Operator', fOperator, setFOperator, (filterOptions.data?.operators ?? []).map((o) => ({ value: o.id, label: o.name }))],
              ['Model', fModel, setFModel, (filterOptions.data?.models ?? []).map((m) => ({ value: m, label: m }))],
              ['Tail number', fTail, setFTail, (filterOptions.data?.tails ?? []).map((t) => ({ value: t, label: t }))],
              ['Airport', fAirport, setFAirport, (filterOptions.data?.airports ?? []).map((a) => ({ value: a.code, label: a.label, search: [a.label, a.iata, a.icao].filter(Boolean).join(' ') }))],
            ] as [string, string | undefined, (v: string | undefined) => void, { value: string; label: string; search?: string }[]][]
          ).map(([label, value, set, options]) => (
            <Select
              key={label}
              showSearch
              allowClear
              placeholder={label}
              aria-label={label}
              value={value}
              loading={filterOptions.isLoading}
              options={options}
              filterOption={(input, o) =>
                ((o as any)?.search ?? (o as any)?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={(v) => {
                set(v ?? undefined);
                setPage(1);
              }}
              style={{ flex: '1 1 130px', minWidth: 120, maxWidth: 220 }}
              // Admin regression 6 Oct — the list grows with its longest
              // option, so long airport names made it 1,981px wide and pushed
              // it off screen. Capped at 360px: an option that does not fit is
              // cut with "…" (antd's option ellipsis) and its full text is the
              // option's title on hover.
              popupMatchSelectWidth={false}
              styles={{ popup: { root: { maxWidth: FILTER_POPUP_MAX_WIDTH } } }}
              optionRender={(o) => <span title={String(o.label ?? '')}>{o.label}</span>}
            />
          ))}
          <Segmented<BookingWhen>
            value={when}
            // SHAR-848 — one write, not two. `setSearchParams` hands the
            // functional updater the router's CURRENT params, not a queued
            // value the way useState does, so a `setWhen` followed by a
            // `setPage` in the same handler had the second silently discard the
            // first: the tab never reached the URL. setWhen resets the page
            // itself.
            onChange={(v) => setWhen(v)}
            options={[
              { label: 'Upcoming', value: 'upcoming' },
              { label: 'Past', value: 'past' },
            ]}
            style={{ marginLeft: 'auto' }}
          />
        </div>
        <Table<AdminBookingRow>
          rowKey="id"
          loading={list.isLoading}
          dataSource={list.data?.data || []}
          onRow={(r) => ({
            // SHAR-848 — hand the detail page the exact list URL to come back to.
            onClick: () => nav(`/bookings/${r.id}`, { state: { from: `${loc.pathname}${loc.search}` } }),
            style: { cursor: 'pointer' },
          })}
          // Regression 29 Sep - the sum of the declared column widths (was 1,950
          // while the columns summed 2,050; 2,100 with Status at 180).
          scroll={{ x: 2100 }}
          pagination={{
            current: page,
            total: list.data?.total || 0,
            pageSize,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (total) => `${total} bookings`,
          }}
          columns={bookingColumns({ removeAdhoc })}
        />
      </Card>
    </Space>
  );
}
